import React from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Icon,
  IconProps,
  type Ryecon,
  RyeconEyeClosed,
  RyeconHelp,
  RyeconLocked,
  RyeconShieldLock,
  RyeconUnlocked,
  Tooltip,
  TooltipProps,
} from '@dbt-labs/sourdough';

/**
 * List of svgs in sinter/static/img/icons/.
 * Should map to all values of "access" for model nodes (currently only models have access).
 */
export const accessTypes = [
  'private',
  'protected',
  'public',
  'no_access',
  'no_permission',
] as const;

export type AccessType = (typeof accessTypes)[number];

type SvgClassName = React.SVGAttributes<SVGSVGElement>['className'];

type SvgParams = {
  className?: SvgClassName;
};

export type AccessIconProps = {
  access: AccessType | undefined;
  isSolid?: boolean;
  tooltipConfig?: Omit<TooltipProps, 'content' | 'children'>;
  iconClassName?: string;
} & SvgParams;

export function getAccessType<TDefaultValue extends undefined | AccessType>(
  accessType: string | undefined,
  defaultValue?: TDefaultValue,
): AccessType | TDefaultValue {
  const index = accessTypes.findIndex((item) => item === accessType);
  return accessTypes[index] || defaultValue;
}

type Detail = {
  tooltip: string;
  ryecon: Ryecon;
};

const detailMap: Record<AccessType, Detail> = {
  private: {
    tooltip: 'Access Level: Private',
    ryecon: RyeconLocked,
  },
  protected: {
    tooltip: 'Access Level: Protected',
    ryecon: RyeconShieldLock,
  },
  public: {
    tooltip: 'Access Level: Public',
    ryecon: RyeconUnlocked,
  },
  no_access: {
    tooltip: 'No Access',
    ryecon: RyeconEyeClosed,
  },
  no_permission: {
    tooltip: 'No Permission',
    ryecon: RyeconEyeClosed,
  },
};

const unknownAccess: Detail = {
  tooltip: 'Access Level: Unknown',
  ryecon: RyeconHelp,
};

/**
 * Access should be: `private`, `protected`, `public`, or `no_access`.
 */
export const AccessLevelIcon: React.FC<
  Omit<IconProps, 'icon' | 'statusIconVariant' | 'ryecon'> & AccessIconProps
> = ({
  access,
  className: classNameOverrides,
  tooltipConfig,
  iconClassName,
  ...props
}) => {
  const iconName = getAccessType(access);
  if (iconName === undefined) return null;
  const className = twMerge(classNameOverrides);

  const { ryecon, tooltip } = detailMap[iconName] ?? unknownAccess;

  return (
    <span className={twMerge('inline-block leading-none', className)}>
      <Tooltip {...tooltipConfig} content={tooltip}>
        <Icon
          {...props}
          ryecon={ryecon}
          className={twMerge('inline-block', iconClassName)}
          alt={tooltip}
        />
      </Tooltip>
    </span>
  );
};

const accessTypeSentenceMap: Record<AccessType, string> = {
  private: 'Private',
  protected: 'Protected',
  public: 'Public',
  no_access: 'No access',
  no_permission: 'No permission',
};

export const accessToSentenceCase = (access: AccessType) => {
  return accessTypeSentenceMap[access];
};
